import { InputLabel, MenuItem } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ValoreDominioVO } from 'shell/models/act-wizard';
import { HttpResponse } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';

export interface ItemTemplateVM extends ItemVM {
  valueTemplate?: string;
  codiceGenere?: string;
}

interface ListTemplateTranscriptionProps {
  onChange: (value?: ItemTemplateVM) => void;
  value: string;
  tipoEvento?: string;
  idUseCase?: string;
  label?: string;
  idEvento?: number;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  isSmallSize?: boolean;
  idDominio?: string;
  toInitializeId?: string;
  onChangeInitialize?: (value?: ItemTemplateVM) => void;
}

const ListTemplateTranscriptionComponent = ({
  onChange,
  onChangeInitialize,
  value,
  tipoEvento,
  idUseCase,
  idEvento,
  label = 'Template trascrizione',
  disabled = false,
  required = false,
  error = false,
  isSmallSize = false,
  idDominio = '101',
  toInitializeId = '',
}: ListTemplateTranscriptionProps): React.ReactElement => {
  const [templates, getTempletes] = useHttpFetch<
    HttpResponse<Array<ValoreDominioVO>>,
    Array<ItemTemplateVM>
  >(
    `${__ACT_API_URL__}/valori-dominio-atti/dominio/${idDominio}/idValoreAnsc/${
      idUseCase || tipoEvento
    }/idEvento/${idEvento}`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: (response) =>
        ((response.data.payload || []) as Array<ValoreDominioVO>).map((item) => ({
          id: item.key?.idValore,
          value: item.descrizioneBreveValore,
          valueTemplate: item.valore,
          codiceGenere: item.codiceGenere,
        })) as Array<ItemVM>,
    }
  );

  useEffect(() => {
    if ((tipoEvento || idUseCase) && idEvento) {
      getTempletes();
    }
  }, [tipoEvento, idEvento, idUseCase]);

  const templatesData: Array<ItemTemplateVM> = useMemo(() => {
    return (templates.data || []) as Array<ItemTemplateVM>;
  }, [templates.data]);

  useEffect(() => {
    if (toInitializeId && onChangeInitialize) {
      onChangeInitialize(templatesData.find((item) => item.id === toInitializeId));
    }
  }, [toInitializeId, templatesData]);

  const disabledComponent: boolean = useMemo(() => {
    return disabled || !Boolean(tipoEvento || idUseCase);
  }, [disabled, tipoEvento, idUseCase]);

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabledComponent} id="template-label">
        {required ? `${label} *` : label}
      </InputLabel>
      <PmiSelect
        labelId="template-label"
        id="template"
        name="template"
        value={value}
        label={required ? `${label} *` : label}
        onChange={(event) =>
          onChange(templatesData?.find((item) => item.id === event.target.value))
        }
        disabled={disabledComponent}
        error={error}
        size={isSmallSize ? 'small' : undefined}
      >
        {templatesData?.map((template: ItemTemplateVM) => (
          <MenuItem key={template.id} value={template.id}>
            {template.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ListTemplateTranscriptionComponent.displayName = 'List Template Transcription component';

const ListTemplateTranscription = memo(ListTemplateTranscriptionComponent);

export default ListTemplateTranscription as typeof ListTemplateTranscriptionComponent;
